import React, { useState } from 'react';
import jwt from 'jsonwebtoken'; // Import jsonwebtoken for decoding
import crypto from 'crypto'; // Import crypto for HMAC signature generation
import logo from '../assets/logo.png'; // Main logo
import logo2 from '../assets/logo2.png'; // Additional logo
import './CSS/SecretPage.css'; // Add your custom styles here

const SecretPage = () => {
  const [jwtToken, setJwtToken] = useState('');
  const [secretKey, setSecretKey] = useState(''); // State for the secret key
  const [decodedHeader, setDecodedHeader] = useState(null);
  const [decodedPayload, setDecodedPayload] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [generatedSignature, setGeneratedSignature] = useState(''); // Store the generated signature
  const [completeToken, setCompleteToken] = useState(''); // Store the complete JWT token

  const handleTokenChange = (e) => {
    setJwtToken(e.target.value);
  };

  const handleSecretKeyChange = (e) => {
    setSecretKey(e.target.value);
  };

  const handleDecode = () => {
    try {
      // Decode the JWT without verifying the signature to extract the header and payload
      const decoded = jwt.decode(jwtToken, { complete: true });

      if (!decoded) {
        setErrorMessage('Invalid JWT token.');
        setDecodedHeader(null);
        setDecodedPayload(null);
        setGeneratedSignature('');
        setCompleteToken('');
        return;
      }

      // Set the decoded token header and payload
      setDecodedHeader(decoded.header);
      setDecodedPayload(decoded.payload);
      setErrorMessage('');

      // Generate the correct signature if a secret key is provided
      if (secretKey) {
        try {
          // Manually create the signature using HMAC with the secret key
          const base64UrlHeader = Buffer.from(JSON.stringify(decoded.header)).toString('base64').replace(/=/g, '');
          const base64UrlPayload = Buffer.from(JSON.stringify(decoded.payload)).toString('base64').replace(/=/g, '');
          const dataToSign = `${base64UrlHeader}.${base64UrlPayload}`;

          // Generate HMAC signature using the provided secret key
          const signature = crypto
            .createHmac('sha256', secretKey) // You can adjust this to match your JWT's algorithm (e.g., 'sha256' for HS256)
            .update(dataToSign)
            .digest('base64')
            .replace(/=/g, '') // Base64 URL encoding
            .replace(/\+/g, '-')
            .replace(/\//g, '_');

          setGeneratedSignature(signature);

          // Generate the complete JWT token with the new signature (without spaces)
          const completeJwtToken = `${base64UrlHeader}.${base64UrlPayload}.${signature}`;
          setCompleteToken(completeJwtToken);
        } catch (e) {
          setGeneratedSignature('Error generating signature');
          setCompleteToken('');
        }
      }
    } catch (error) {
      // Handle the error if decoding fails
      setDecodedHeader(null);
      setDecodedPayload(null);
      setGeneratedSignature('');
      setCompleteToken('');
      setErrorMessage('Invalid JWT token or server error.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(completeToken).then(() => {
      alert('JWT token copied to clipboard!');
    });
  };

  return (
    <div className="secret-page-container">
      <div className="logo-container">
        <img src={logo} alt="Main Logo" className="logo-main" />
      </div>

      <h1>JWT Decoder and Token Generator</h1>
      <p>Enter your JWT token to decode and generate the complete token with your secret key:</p>
      <div className="jwt-input-container">
        <input
          type="text"
          className="jwt-input"
          placeholder="Paste your JWT token here (without signature)..."
          value={jwtToken}
          onChange={handleTokenChange}
        />
      </div>

      <div className="secret-key-container">
        <input
          type="text"
          className="secret-key-input"
          placeholder="Enter your secret key..."
          value={secretKey}
          onChange={handleSecretKeyChange}
        />
      </div>

      <button onClick={handleDecode} className="decode-button">
        Decode and Generate Token
      </button>

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {decodedHeader && (
        <div className="decoded-token-container">
          <h3>Decoded JWT Header</h3>
          <pre className="decoded-token">
            {JSON.stringify(decodedHeader, null, 2)}
          </pre>
        </div>
      )}

      {decodedPayload && (
        <div className="decoded-token-container">
          <h3>Decoded JWT Payload</h3>
          <pre className="decoded-token">
            {JSON.stringify(decodedPayload, null, 2)}
          </pre>
        </div>
      )}

      {generatedSignature && (
        <div className="generated-signature-container">
          <h3>Generated JWT Signature</h3>
          <pre className="generated-signature">{generatedSignature}</pre>
        </div>
      )}

      {completeToken && (
        <div className="complete-token-container">
          <h3>Complete JWT Token (Formatted)</h3>
          <pre className="complete-token">
            {completeToken.split('.').join('\n.')}
          </pre>
          <button onClick={copyToClipboard} className="copy-button">
            Copy Token
          </button>
        </div>
      )}
      <div className="logos-container">
        <div>Powered by</div>
        <img src={logo2} alt="Logo 2" className="logo-small" />
      </div>
    </div>
  );
};

export default SecretPage;
